import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Search as SearchIcon, MapPin, Star, SlidersHorizontal, X } from 'lucide-react';
import { getVehicles, getCategories } from '../api';

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [priceRange, setPriceRange] = useState([0, 3000]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const allFeatures = ['Automatic', 'Manual', 'Chauffeur Available', 'GPS Navigation', 'Premium Sound', 'Sport Mode'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehiclesData, categoriesData] = await Promise.all([
          getVehicles(),
          getCategories()
        ]);
        setVehicles(vehiclesData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      const matchesSearch = vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           vehicle.location.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || vehicle.category.toLowerCase().includes(selectedCategory);
      const matchesPrice = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];
      const matchesFeatures = selectedFeatures.length === 0 || 
                             selectedFeatures.some(feature => vehicle.features.includes(feature));
      
      return matchesSearch && matchesCategory && matchesPrice && matchesFeatures;
    });
  }, [vehicles, searchQuery, selectedCategory, priceRange, selectedFeatures]);

  const handleFeatureToggle = (feature) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setPriceRange([0, 3000]);
    setSelectedFeatures([]);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search by vehicle or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-red-600 focus:ring-red-600"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 border-black text-black hover:bg-black hover:text-white transition-all"
            >
              <SlidersHorizontal className="mr-2 h-5 w-5" />
              Filters
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          {showFilters && (
            <Card className="w-80 h-fit p-6 sticky top-24 border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-black">Filters</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700">
                  Clear All
                </Button>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <Label className="text-sm font-semibold text-black mb-3 block">Category</Label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Checkbox
                      id="all"
                      checked={selectedCategory === 'all'}
                      onCheckedChange={() => setSelectedCategory('all')}
                      className="border-gray-300"
                    />
                    <label htmlFor="all" className="ml-2 text-sm text-gray-700 cursor-pointer">All Categories</label>
                  </div>
                  {categories.map(category => (
                    <div key={category.id} className="flex items-center">
                      <Checkbox
                        id={category.id}
                        checked={selectedCategory === category.id}
                        onCheckedChange={() => setSelectedCategory(category.id)}
                        className="border-gray-300"
                      />
                      <label htmlFor={category.id} className="ml-2 text-sm text-gray-700 cursor-pointer">
                        {category.name} ({category.count})
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <Label className="text-sm font-semibold text-black mb-3 block">
                  Price Range: ${priceRange[0]} - ${priceRange[1]}/day
                </Label>
                <Slider
                  min={0}
                  max={3000}
                  step={50}
                  value={priceRange}
                  onValueChange={setPriceRange}
                  className="mb-2"
                />
              </div>

              {/* Features */}
              <div>
                <Label className="text-sm font-semibold text-black mb-3 block">Features</Label>
                <div className="space-y-2">
                  {allFeatures.map(feature => (
                    <div key={feature} className="flex items-center">
                      <Checkbox
                        id={feature}
                        checked={selectedFeatures.includes(feature)}
                        onCheckedChange={() => handleFeatureToggle(feature)}
                        className="border-gray-300"
                      />
                      <label htmlFor={feature} className="ml-2 text-sm text-gray-700 cursor-pointer">{feature}</label>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Results */}
          <div className="flex-1">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-black">
                {filteredVehicles.length} Vehicles Available
              </h2>
              {(selectedCategory !== 'all' || selectedFeatures.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedCategory !== 'all' && (
                    <div className="flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1 rounded-full text-sm">
                      <span>{categories.find(c => c.id === selectedCategory)?.name}</span>
                      <X 
                        className="h-4 w-4 cursor-pointer" 
                        onClick={() => setSelectedCategory('all')}
                      />
                    </div>
                  )}
                  {selectedFeatures.map(feature => (
                    <div key={feature} className="flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1 rounded-full text-sm">
                      <span>{feature}</span>
                      <X 
                        className="h-4 w-4 cursor-pointer" 
                        onClick={() => handleFeatureToggle(feature)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredVehicles.map(vehicle => (
                <Card
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                  className="overflow-hidden cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-0 group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={vehicle.image}
                      alt={vehicle.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-black/80 backdrop-blur px-3 py-1 rounded-full">
                      <span className="text-white font-semibold">${vehicle.price}/day</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-black group-hover:text-red-600 transition-colors">{vehicle.name}</h3>
                        <p className="text-sm text-gray-500">{vehicle.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{vehicle.location}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {vehicle.features.slice(0, 3).map(feature => (
                        <span key={feature} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 mr-1" />
                        <span className="font-semibold text-black">{vehicle.rating}</span>
                        <span className="text-gray-500 ml-1">({vehicle.reviewCount})</span>
                      </div>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {filteredVehicles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">No vehicles found matching your criteria.</p>
                <Button onClick={clearFilters} className="mt-4 bg-red-600 hover:bg-red-700 text-white">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;
